import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import OCGatewayClient from "../client/OCGatewayClient";
import { gatewayUserScopedQueryOptions } from "./gatewayQueryUtils";

/**
 * Pet profile avatars travel over the gateway's Bearer-authenticated REST
 * routes (see onlycat-gateway src/restHandlers/rfidProfiles.ts), not over
 * socket.io: uploads are raw image bodies, reads are binary responses.
 * Because the GET requires an Authorization header, the image cannot be an
 * <img src> URL — it is fetched here and exposed as an object URL.
 *
 * `avatarUpdatedAt` (from the RfidProfile) is the source of truth for "has
 * an avatar" and versions the cache key, so a re-upload busts the old image.
 */

/**
 * Object URL of a profile's current avatar, or null when it has none.
 * `ownerUserId` is the avatar owner's id — `avatarUserId` on the merged
 * profile, which may be any member of the device-sharing household.
 */
export function useRfidProfileAvatarQuery(
    ownerUserId: number | undefined,
    rfidCode: string,
    avatarUpdatedAt: Date | string | null | undefined
) {
    const version = avatarUpdatedAt ? new Date(avatarUpdatedAt).getTime() : null;

    return useQuery({
        queryKey: ['rfidProfileAvatar', rfidCode, ownerUserId, version].filter((part) => part != null),
        queryFn: async (): Promise<string | null> => {
            const response = await OCGatewayClient.httpRequest(
                'GET',
                `/rfid-profiles/${ownerUserId}/${encodeURIComponent(rfidCode)}/avatar?v=${version}`
            );

            if (response.status === 404) {
                return null;
            }

            if (!response.ok) {
                throw new Error(`Avatar request failed (${response.status})`);
            }

            const blob = await response.blob();
            return URL.createObjectURL(blob);
        },
        enabled: !!ownerUserId && !!rfidCode && !!version,
        ...gatewayUserScopedQueryOptions,
        staleTime: Infinity
    });
}

export function useRfidProfileAvatarUploadMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ rfidCode, image }: { rfidCode: string; image: Blob }) => {
            const response = await OCGatewayClient.httpRequest(
                'PUT',
                `/rfid-profiles/${encodeURIComponent(rfidCode)}/avatar`,
                image
            );

            if (!response.ok) {
                throw new Error(await response.text());
            }
        },
        onSettled: (data, error, variables) => {
            // avatarUpdatedAt lives on the profile; refetching it re-keys the
            // avatar query to the new version.
            queryClient.invalidateQueries({ queryKey: ['getRfidProfile', variables.rfidCode] });
        }
    });
}

export function useRfidProfileAvatarDeleteMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ rfidCode }: { rfidCode: string }) => {
            const response = await OCGatewayClient.httpRequest(
                'DELETE',
                `/rfid-profiles/${encodeURIComponent(rfidCode)}/avatar`
            );

            if (!response.ok) {
                throw new Error(await response.text());
            }
        },
        onSettled: (data, error, variables) => {
            queryClient.invalidateQueries({ queryKey: ['getRfidProfile', variables.rfidCode] });
        }
    });
}
